/**
 * Resized, re-encoded image URLs.
 *
 * Why this exists: files.remapp.ae serves originals only. Every resize
 * parameter we tried (?w, ?width, ?resize, ?tr=, ?imwidth, /cdn-cgi/image/)
 * returns the byte-identical original — a single project cover is 571 KB at
 * 7000px wide for a slot that renders 662px. srcset alone cannot help when the
 * origin has only one size, so the URLs are rewritten through a resizing proxy.
 *
 * Switching providers: change PROVIDER below.
 *   'weserv'  — images.weserv.nl. No account, no config, returns WebP with
 *               Cache-Control: public, max-age=31536000. Currently in use.
 *   'vercel'  — Vercel's own optimizer, which also negotiates AVIF. Needs an
 *               `images` block in vercel.json listing files.remapp.ae under
 *               remotePatterns, and it bills per source image. Verified NOT
 *               enabled today: /_vercel/image returns the SPA shell.
 */

const PROVIDER = 'weserv';

/** Widths we are willing to generate. Keep this list short — each entry is a
 *  separate origin fetch and a separate cache entry at the proxy. */
export const IMAGE_WIDTHS = [320, 480, 640, 960, 1280, 1600];

/** Sources that must never be rewritten: local assets, blobs, inline data. */
function isRewritable(src) {
    if (typeof src !== 'string' || !src) return false;
    if (!/^https?:\/\//i.test(src)) return false;             // relative, data:, blob:
    if (/(^|\/\/)images\.weserv\.nl/i.test(src)) return false; // already proxied
    if (/\.svg(\?|$)/i.test(src)) return false;                // vectors gain nothing
    return true;
}

/**
 * One resized URL.
 *
 * @param {string} src     Original absolute image URL.
 * @param {number} width   Target width in CSS pixels.
 * @param {object} [opts]
 * @param {number} [opts.quality=78]
 * @returns {string} A URL safe to drop straight into src.
 */
export function imageUrl(src, width, opts = {}) {
    if (!isRewritable(src)) return src || '';

    const { quality = 78 } = opts;

    if (PROVIDER === 'vercel') {
        return `/_vercel/image?url=${encodeURIComponent(src)}&w=${width}&q=${quality}`;
    }

    // `we` = never enlarge past the original; `output=webp` because weserv has
    // AVIF disabled ("Supported savers: jpg, png, webp, tiff, gif, json, jxl").
    return `https://images.weserv.nl/?url=${encodeURIComponent(src)}` +
        `&w=${width}&output=webp&q=${quality}&we`;
}

/**
 * A srcset across IMAGE_WIDTHS, so the browser picks by viewport and DPR.
 *
 * @param {string} src
 * @param {number[]} [widths]
 * @returns {string|undefined} undefined when the source cannot be resized —
 *          React then omits the attribute rather than rendering srcset="".
 */
export function imageSrcSet(src, widths = IMAGE_WIDTHS) {
    if (!isRewritable(src)) return undefined;
    return widths.map((w) => `${imageUrl(src, w)} ${w}w`).join(', ');
}

/** Host to warm up in index.html, so the first image request skips DNS+TLS. */
export const IMAGE_HOST = PROVIDER === 'vercel' ? null : 'https://images.weserv.nl';
